import { Suspense, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';
import { MemberMe } from 'src/modules/member/models/member';
import { useTranslation } from 'react-i18next';
import { RootState } from '../services/store';
import { RoutesEnum } from '../models/enums/routes.enum';
import { useGlobals } from '../contexts/appContext';
import { getMemberSettings } from '../services/memberService';
import ConsoleHelper from '../functions/consoleHelper';
import { UserAccountTypeEnum } from '../models/enums/userAccountType.enum';
import { buildLoginSearch } from '../functions/returnUrl';
import { useSilentSignin } from '../hooks/useSilentSignin';
import { SystemMessageTypeEnum } from '../models/enums/systemMessageType.enum';
import { MemberStatusEnum } from '../models/enums/memberStatusEnum';
import { ResignedMemberNotice } from '../components/organisms/resignedMemberNotice';

const RequireMember = ({ children }: { children: JSX.Element }) => {
  const location = useLocation();
  const { t } = useTranslation();
  const user = useSelector((state: RootState) => state.oidc.user);
  const { setCurrentMember, setSystemMessage } = useGlobals();

  const [isLoading, setIsLoading] = useState(true);
  // Utträdd medlem (samma status som visas som "Utträdd" i registret) → hela Min sida-portalen låses.
  const [isResigned, setIsResigned] = useState(false);
  // Ett vänmedlemskap är en egen medlemspost och kan därför logga in som vilken medlem som helst.
  // Den posten ska inte komma åt Min sida, medlemmen använder sitt ordinarie medlemskap.
  const [isExtraMember, setIsExtraMember] = useState(false);

  useEffect(() => {
    const fetchMember = async () => {
      const result = await getMemberSettings();
      const memberResult = result.data as MemberMe;
      setCurrentMember(memberResult);
      setIsResigned(memberResult.status?.id === MemberStatusEnum.NotMember);
      setIsExtraMember(!!memberResult.parentMembership);
      const countUnpaidInvoices = memberResult.currentInvoices?.filter((x) => x.paid === 0) || [];
      if (countUnpaidInvoices?.length > 0) {
        setSystemMessage({
          height: 40,
          message:
            countUnpaidInvoices.length === 1
              ? t('common:statusMessages.unPaidInvoice')
              : t('common:statusMessages.unPaidInvoices', { count: countUnpaidInvoices.length }),
          hideable: true,
          type: SystemMessageTypeEnum.UserMessage,
        });
      }

      ConsoleHelper.log('Member/ME', result.data);
      setIsLoading(false);
    };
    if (user && !user.expired && user.profile.accountType === UserAccountTypeEnum.Member) {
      // Medlemmen kan dyka upp efter en tyst inloggning, och då ska sidan vänta på
      // member/me i stället för att ritas med tom medlem.
      setIsLoading(true);
      fetchMember();
    } else {
      setIsLoading(false);
    }
  }, [user, setCurrentMember, setSystemMessage, t]);

  // En ny flik har tom sessionStorage och saknar därför användaren även när man redan är
  // inloggad. Hämta inloggningen tyst innan vi ger upp och visar inloggningssidan.
  const isSigningIn = useSilentSignin(!user || user.expired);

  if (isLoading || isSigningIn) {
    return <div />;
  }
  if (user && !user.expired && user.profile.accountType === UserAccountTypeEnum.Member) {
    if (isResigned) {
      return <ResignedMemberNotice />;
    }
    if (isExtraMember) {
      return <ResignedMemberNotice variant='extraMember' />;
    }
    return <Suspense fallback={<div />}>{children}</Suspense>;
  }
  if (user && !user.expired && user.profile.accountType === UserAccountTypeEnum.User) {
    return <Navigate to={RoutesEnum.Dashboard} state={{ from: location }} replace />;
  }
  return <Navigate to={`${RoutesEnum.Login}${buildLoginSearch(location)}`} state={{ from: location }} replace />;
};

export default RequireMember;
